import { Component } from '@theme/component';
import { debounce, isClickedOutside } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {number} */
  #openRequestId = 0;

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {boolean} */
  #listenersBound = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    this.#openRequestId += 1;
    this.#unbindChromeListeners();

    const { dialog } = this.refs;
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      try {
        dialog.close();
      } catch {
        // ignore
      }
    }

    this.#unlockBodyScroll();
    syncDialogOpenState();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * @param {HTMLDialogElement} dialog
   * @returns {number}
   */
  #getCloseDurationMs(dialog) {
    const style = getComputedStyle(dialog);
    const duration = Number.parseFloat(String(style.animationDuration).split(',')[0] ?? '');
    const delay = Number.parseFloat(String(style.animationDelay).split(',')[0] ?? '');

    if (!Number.isNaN(duration) && duration > 0) {
      return Math.min(duration * 1000 + (Number.isNaN(delay) ? 0 : delay * 1000), 600);
    }

    const customDuration =
      style.getPropertyValue('--drawer-panel-duration').trim() ||
      style.getPropertyValue('--facets-drawer-animation-duration').trim();
    const parsed = Number.parseFloat(customDuration);

    return Number.isNaN(parsed) ? 480 : Math.min(parsed * 1000, 600);
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @returns {Promise<void>}
   */
  #waitForCloseAnimation(dialog) {
    const closeDurationMs = this.#getCloseDurationMs(dialog);

    return new Promise((resolve) => {
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        dialog.removeEventListener('animationend', onAnimationEnd);
        clearTimeout(fallbackTimer);
        resolve(undefined);
      };

      /** @param {AnimationEvent} event */
      const onAnimationEnd = (event) => {
        if (event.target !== dialog) return;
        finish();
      };

      dialog.addEventListener('animationend', onAnimationEnd);
      const fallbackTimer = setTimeout(finish, closeDurationMs + 50);
    });
  }

  #lockBodyScroll(scrollY) {
    const resolvedScrollY = Number.isFinite(scrollY) ? scrollY : window.scrollY;
    this.#previousScrollY = resolvedScrollY;
    // Shared across dialog handoffs (quick-add → cart) so unlock always restores the right Y.
    document.documentElement.dataset.scrollLockY = String(resolvedScrollY);
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${resolvedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  #unlockBodyScroll() {
    // Nested dialogs (e.g. quick-add over cart drawer): keep lock while another dialog is fully open.
    if (document.querySelector('dialog[open]:not(.dialog-closing)')) return;

    const wasLocked = document.body.style.position === 'fixed';
    if (!wasLocked) return;

    const fromDataset = Number.parseInt(document.documentElement.dataset.scrollLockY || '', 10);
    const fromTop = Math.abs(Number.parseInt(document.body.style.top || '0', 10));
    const scrollY = Number.isFinite(fromDataset)
      ? fromDataset
      : Number.isFinite(this.#previousScrollY) && this.#previousScrollY > 0
        ? this.#previousScrollY
        : fromTop;
    const html = document.documentElement;

    // Theme sets html { scroll-behavior: smooth } — that animates scrollTo from 0→Y as a
    // visible top-to-bottom whip. Force instant restore for this unlock only.
    html.style.scrollBehavior = 'auto';

    document.body.style.removeProperty('width');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    delete html.dataset.scrollLockY;

    if (Number.isFinite(scrollY) && scrollY >= 0) {
      window.scrollTo(0, scrollY);
    }

    requestAnimationFrame(() => {
      html.style.removeProperty('scroll-behavior');
    });
  }

  #bindChromeListeners() {
    if (this.#listenersBound) return;
    this.#listenersBound = true;
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
  }

  #unbindChromeListeners() {
    if (!this.#listenersBound) return;
    this.#listenersBound = false;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Shows the dialog.
   * @param {{ immediate?: boolean }} [options] - Pass `{ immediate: true }` to open in the same turn (cart handoff).
   */
  showDialog(options = {}) {
    const { dialog } = this.refs;

    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.open) return;

    const requestId = ++this.#openRequestId;
    // If body is already locked, window.scrollY is 0 — read the locked offset instead.
    const fromDataset = Number.parseInt(document.documentElement.dataset.scrollLockY || '', 10);
    const fromTop = Math.abs(Number.parseInt(document.body.style.top || '0', 10));
    const scrollY =
      document.body.style.position === 'fixed'
        ? (Number.isFinite(fromDataset) ? fromDataset : fromTop) || this.#previousScrollY || 0
        : window.scrollY;

    const open = () => {
      if (requestId !== this.#openRequestId) return;
      if (dialog.open) return;

      this.#lockBodyScroll(scrollY);

      try {
        dialog.showModal();
      } catch {
        // showModal can fail when an ancestor is display:none / not rendered.
        this.#unlockBodyScroll();
        syncDialogOpenState();
        return;
      }

      syncDialogOpenState();
      this.dispatchEvent(new DialogOpenEvent());
      this.#bindChromeListeners();
    };

    if (options.immediate) {
      open();
      return;
    }

    requestAnimationFrame(open);
  }

  /**
   * Closes the dialog without waiting for the exit animation.
   * Use when handing off to another modal (e.g. cart drawer after add).
   * @param {{ keepBodyLock?: boolean }} [options]
   */
  closeDialogImmediate = (options = {}) => {
    const keepBodyLock = Boolean(options.keepBodyLock);
    const { dialog } = this.refs;

    this.#openRequestId += 1;
    this.#unbindChromeListeners();

    if (!(dialog instanceof HTMLDialogElement)) {
      if (!keepBodyLock) this.#unlockBodyScroll();
      syncDialogOpenState();
      this.dispatchEvent(new DialogCloseEvent());
      return;
    }

    dialog.classList.remove('dialog-closing');

    if (dialog.open) {
      try {
        dialog.close();
      } catch {
        // ignore
      }
    }

    if (keepBodyLock && document.body.style.position === 'fixed') {
      // Preserve scroll lock + dialog-open chrome for the next modal in the same turn.
      document.documentElement.setAttribute('data-dialog-open', '');
    } else {
      this.#unlockBodyScroll();
      syncDialogOpenState();
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    this.#openRequestId += 1;
    this.#unbindChromeListeners();

    if (!(dialog instanceof HTMLDialogElement)) {
      this.#unlockBodyScroll();
      syncDialogOpenState();
      return;
    }

    if (!dialog.open) {
      dialog.classList.remove('dialog-closing');
      this.#unlockBodyScroll();
      syncDialogOpenState();
      return;
    }

    if (dialog.classList.contains('dialog-closing')) {
      try {
        dialog.close();
      } catch {
        // ignore
      }
      dialog.classList.remove('dialog-closing');
      this.#unlockBodyScroll();
      syncDialogOpenState();
      return;
    }

    dialog.classList.add('dialog-closing');
    void dialog.offsetWidth;
    // Keep data-dialog-open until unlock — clearing it early while body is fixed
    // reflows sticky PLP chrome and causes a jump-to-top on close.

    try {
      await this.#waitForCloseAnimation(dialog);
    } finally {
      try {
        if (dialog.open) dialog.close();
      } catch {
        // ignore
      }
      dialog.classList.remove('dialog-closing');
      this.#unlockBodyScroll();
      syncDialogOpenState();
      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

function syncDialogOpenState() {
  if (document.querySelector('dialog[open]:not(.dialog-closing)')) {
    document.documentElement.setAttribute('data-dialog-open', '');
    return;
  }

  // While a dialog is animating closed, keep the attribute so sticky PLP chrome
  // does not reflow under a position:fixed body (that caused jump-to-top).
  if (document.querySelector('dialog[open].dialog-closing')) {
    document.documentElement.setAttribute('data-dialog-open', '');
    return;
  }

  document.documentElement.removeAttribute('data-dialog-open');
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
